import React from 'react'
import { Button, Heading, Text, LoadingSpinner, Input, Select, SortableTableHeader } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { modulesService } from '../services/modules'
import { cctParametrosService } from '../services/cctParametros'
import { userLogsService } from '../services/userLogs'
import type { CctClausula, CctClausulaConvencaoRow, CctClausulaInput } from '../types/cctParametros'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { CheckboxMultiSelect } from './Qualidade/CheckboxMultiSelect'

const STATUS_FILTER_OPTIONS = [
  { value: 'DISPONÍVEL PARA IMPORTAR', label: 'DISPONÍVEL PARA IMPORTAR' },
  { value: 'IMPORTADO SCGP', label: 'IMPORTADO SCGP' },
  { value: 'NÃO LOCALIZADA', label: 'NÃO LOCALIZADA' },
]

const MODULE_NAME = 'ccts_clausulas'

type ActiveTab = 'clausulas_cct' | 'cadastro'

type ConvencaoSortKey =
  | 'id_scgp'
  | 'clausula'
  | 'codigo'
  | 'status'
  | 'num_vidas'
  | 'vigencia_inicio'
  | 'vigencia_fim'
  | 'clausulas'

const EMPTY_INPUT: CctClausulaInput = { nome: '', descricao: '' }

const formatClausulas = (values: number[] | null): string =>
  values && values.length > 0 ? values.join(', ') : '—'

const formatDateShort = (dateString: string | null | undefined): string => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

const normalizeBuscaStatus = (status: string | null | undefined): string => {
  const bruto = (status || '').trim().toUpperCase()
  if (
    bruto === 'NÃO LOCALIZADA MTE' ||
    bruto === 'NAO LOCALIZADA MTE' ||
    bruto === 'NÃO LOCALIZADA' ||
    bruto === 'NAO LOCALIZADA'
  ) {
    return 'NÃO LOCALIZADA'
  }
  if (bruto === 'IMPORTADO SCGP' || bruto === 'IMPORTADO') {
    return 'IMPORTADO SCGP'
  }
  if (bruto === 'DISPONÍVEL PARA IMPORTAR' || bruto === 'DISPONIVEL PARA IMPORTAR') {
    return 'DISPONÍVEL PARA IMPORTAR'
  }
  return bruto
}

const getBuscaStatusDisplay = (
  status: string | null | undefined,
): { label: string; className: string } => {
  const label = normalizeBuscaStatus(status) || '—'
  let className = 'text-gray-700 dark:text-gray-300'

  if (label === 'NÃO LOCALIZADA') {
    className = 'text-yellow-600 dark:text-yellow-400'
  } else if (label === 'DISPONÍVEL PARA IMPORTAR') {
    className = 'text-blue-600 dark:text-blue-400'
  } else if (label === 'IMPORTADO SCGP') {
    className = 'text-green-600 dark:text-green-400'
  }

  return { label, className }
}

const renderBuscaStatus = (status: string | null | undefined) => {
  const { label, className } = getBuscaStatusDisplay(status)
  const partes = label.split(' ').filter(Boolean)

  return (
    <span className={`text-sm font-medium ${className}`}>
      {partes.map((parte, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <br />}
          {parte}
        </React.Fragment>
      ))}
    </span>
  )
}

const CCTClausulas: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('clausulas_cct')
  const [loadingCadastro, setLoadingCadastro] = React.useState(false)
  const [loadingConvencoes, setLoadingConvencoes] = React.useState(false)
  const [hasSearchedConvencoes, setHasSearchedConvencoes] = React.useState(false)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [convencoesError, setConvencoesError] = React.useState<string | null>(null)

  const [clausulas, setClausulas] = React.useState<CctClausula[]>([])
  const [convencoes, setConvencoes] = React.useState<CctClausulaConvencaoRow[]>([])
  const [clausulasOptions, setClausulasOptions] = React.useState<CctClausula[]>([])
  const [filter, setFilter] = React.useState('')
  const [filtroIdScgp, setFiltroIdScgp] = React.useState('')
  const [filtroNumeroMte, setFiltroNumeroMte] = React.useState('')
  const [filtroCnpjEntidadePatronal, setFiltroCnpjEntidadePatronal] = React.useState('')
  const [filtroCnpjEntidadeLaboral, setFiltroCnpjEntidadeLaboral] = React.useState('')
  const [filtroClausulaIds, setFiltroClausulaIds] = React.useState<string[]>([])
  const [filtroStatuses, setFiltroStatuses] = React.useState<string[]>([])
  const [filtroDataInicioVigencia, setFiltroDataInicioVigencia] = React.useState('')
  const [filtroDataFimVigencia, setFiltroDataFimVigencia] = React.useState('')
  const [tipoFiltroDataInicio, setTipoFiltroDataInicio] = React.useState<'data' | 'mes' | 'ano'>('data')
  const [tipoFiltroDataFim, setTipoFiltroDataFim] = React.useState<'data' | 'mes' | 'ano'>('data')
  const [operadorFiltroInicioVigencia, setOperadorFiltroInicioVigencia] = React.useState<'igual' | 'a_partir_de'>('a_partir_de')
  const [operadorFiltroFimVigencia, setOperadorFiltroFimVigencia] = React.useState<'igual' | 'ate'>('ate')
  const [convencoesCurrentPage, setConvencoesCurrentPage] = React.useState(1)
  const [convencoesSortBy, setConvencoesSortBy] = React.useState<ConvencaoSortKey>('clausula')
  const [convencoesSortOrder, setConvencoesSortOrder] = React.useState<'asc' | 'desc'>('asc')

  const CONVENCOES_ITEMS_PER_PAGE = 20

  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description: string | null } | null>(null)

  const [formModal, setFormModal] = React.useState<CctClausulaInput | null>(null)
  const [submittingForm, setSubmittingForm] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{ id: number; label: string } | null>(null)
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message
    const maybe = error as { message?: string; code?: string }
    if (maybe?.message) return maybe.message
    if (maybe?.code) return `Erro (${maybe.code})`
    return 'Erro inesperado ao processar solicitação.'
  }

  const loadCadastro = React.useCallback(async () => {
    setLoadingCadastro(true)
    setPageError(null)
    try {
      const data = await cctParametrosService.listClausulas()
      setClausulas(data)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoadingCadastro(false)
    }
  }, [])

  const clausulaFilterOptions = React.useMemo(
    () =>
      [...clausulasOptions]
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }))
        .map((clausula) => ({
          value: String(clausula.id),
          label: clausula.nome,
        })),
    [clausulasOptions],
  )

  const loadClausulasOptions = React.useCallback(async () => {
    try {
      const data = await cctParametrosService.listClausulas()
      setClausulasOptions(data)
    } catch (error) {
      setConvencoesError(getErrorMessage(error))
    }
  }, [])

  const handleBuscarConvencoes = async () => {
    setHasSearchedConvencoes(true)
    setConvencoesCurrentPage(1)
    setLoadingConvencoes(true)
    setConvencoesError(null)
    try {
      const data = await cctParametrosService.listClausulasConvencao({
        idScgp: filtroIdScgp.trim() || undefined,
        numeroMte: filtroNumeroMte.trim() || undefined,
        cnpjEntidadePatronal: filtroCnpjEntidadePatronal.trim() || undefined,
        cnpjEntidadeLaboral: filtroCnpjEntidadeLaboral.trim() || undefined,
        cctClausulaIds: filtroClausulaIds.length
          ? filtroClausulaIds.map((id) => Number(id))
          : undefined,
        statuses: filtroStatuses.length ? filtroStatuses : undefined,
        vigenciaInicio: filtroDataInicioVigencia.trim() || undefined,
        operadorVigenciaInicio: operadorFiltroInicioVigencia,
        vigenciaFim: filtroDataFimVigencia.trim() || undefined,
        operadorVigenciaFim: operadorFiltroFimVigencia,
      })
      setConvencoes(data)
    } catch (error) {
      setConvencoesError(getErrorMessage(error))
      setConvencoes([])
    } finally {
      setLoadingConvencoes(false)
    }
  }

  React.useEffect(() => {
    if (activeTab === 'cadastro') {
      void loadCadastro()
    }
  }, [activeTab, loadCadastro])

  React.useEffect(() => {
    if (activeTab === 'clausulas_cct') {
      void loadClausulasOptions()
    }
  }, [activeTab, loadClausulasOptions])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'CCTs • Cláusulas',
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  const openNew = () => {
    setFormError(null)
    setFormModal({ ...EMPTY_INPUT })
  }

  const openEdit = (item: CctClausula) => {
    setFormError(null)
    setFormModal({
      id: item.id,
      nome: item.nome,
      descricao: item.descricao ?? '',
    })
  }

  const closeFormModal = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
  }

  const handleFormSave = async () => {
    if (!formModal) return
    if (!formModal.nome.trim()) {
      setFormError('O nome é obrigatório.')
      return
    }
    setSubmittingForm(true)
    setFormError(null)
    const trimmedNome = formModal.nome.trim()
    const trimmedDescricao = formModal.descricao.trim()
    const previous = formModal.id
      ? clausulas.find((item) => item.id === formModal.id)
      : undefined
    try {
      await cctParametrosService.upsertClausula({
        ...formModal,
        nome: trimmedNome,
        descricao: trimmedDescricao,
      })
      if (formModal.id && previous) {
        await userLogsService.logUpdate(
          'other',
          String(formModal.id),
          `Alterou cláusula CCT (id ${formModal.id})`,
          {
            nome: previous.nome,
            descricao: previous.descricao,
          },
          {
            nome: trimmedNome,
            descricao: trimmedDescricao || null,
          },
          { module: MODULE_NAME, additional_info: 'CCTs • Cláusulas' },
        )
      }
      closeFormModal()
      await loadCadastro()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  const openDeleteModal = (id: number, label: string) => {
    setDeleteModal({ id, label })
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
  }

  const confirmDelete = async () => {
    if (!deleteModal) return
    const deletedItem = clausulas.find((item) => item.id === deleteModal.id)
    setSubmittingDelete(true)
    try {
      await cctParametrosService.deleteClausula(deleteModal.id)
      await userLogsService.logDelete(
        'other',
        String(deleteModal.id),
        `Excluiu cláusula CCT (id ${deleteModal.id})`,
        {
          module: MODULE_NAME,
          additional_info: 'CCTs • Cláusulas',
          deleted_clausula: {
            id: deleteModal.id,
            nome: deleteModal.label,
            descricao: deletedItem?.descricao ?? null,
          },
        },
      )
      setClausulas((prev) => prev.filter((item) => item.id !== deleteModal.id))
      closeDeleteModal()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingDelete(false)
    }
  }

  const clearConvencoesFilters = () => {
    setFiltroIdScgp('')
    setFiltroNumeroMte('')
    setFiltroCnpjEntidadePatronal('')
    setFiltroCnpjEntidadeLaboral('')
    setFiltroClausulaIds([])
    setFiltroStatuses([])
    setFiltroDataInicioVigencia('')
    setFiltroDataFimVigencia('')
    setTipoFiltroDataInicio('data')
    setTipoFiltroDataFim('data')
    setOperadorFiltroInicioVigencia('a_partir_de')
    setOperadorFiltroFimVigencia('ate')
    setConvencoes([])
    setHasSearchedConvencoes(false)
    setConvencoesCurrentPage(1)
    setConvencoesError(null)
  }

  const hasConvencoesFilters = Boolean(
    filtroIdScgp.trim()
    || filtroNumeroMte.trim()
    || filtroCnpjEntidadePatronal.trim()
    || filtroCnpjEntidadeLaboral.trim()
    || filtroClausulaIds.length > 0
    || filtroStatuses.length > 0
    || filtroDataInicioVigencia.trim()
    || filtroDataFimVigencia.trim(),
  )

  const convencoesOrdenadas = React.useMemo(() => {
    const getSortValue = (item: CctClausulaConvencaoRow, key: ConvencaoSortKey): string | number => {
      switch (key) {
        case 'id_scgp':
          return item.cct_buscas?.id_scgp ?? NaN
        case 'clausula':
          return (item.cct_clausulas?.nome ?? '').toLowerCase()
        case 'codigo':
          return (item.cct_buscas?.codigo ?? '').toLowerCase()
        case 'status':
          return normalizeBuscaStatus(item.cct_buscas?.status).toLowerCase()
        case 'num_vidas':
          return item.num_vidas ?? NaN
        case 'vigencia_inicio':
          return item.cct_buscas?.vigencia_inicio
            ? new Date(item.cct_buscas.vigencia_inicio).getTime()
            : 0
        case 'vigencia_fim':
          return item.cct_buscas?.vigencia_fim
            ? new Date(item.cct_buscas.vigencia_fim).getTime()
            : 0
        case 'clausulas':
          return item.clausulas && item.clausulas.length > 0 ? item.clausulas.join(',') : ''
        default:
          return ''
      }
    }

    return [...convencoes].sort((a, b) => {
      const aVal = getSortValue(a, convencoesSortBy)
      const bVal = getSortValue(b, convencoesSortBy)

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        if (Number.isNaN(aVal) && Number.isNaN(bVal)) return 0
        if (Number.isNaN(aVal)) return 1
        if (Number.isNaN(bVal)) return -1
      }

      if (aVal < bVal) return convencoesSortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return convencoesSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [convencoes, convencoesSortBy, convencoesSortOrder])

  const convencoesTotalPages = Math.ceil(convencoesOrdenadas.length / CONVENCOES_ITEMS_PER_PAGE)
  const convencoesStartIndex = (convencoesCurrentPage - 1) * CONVENCOES_ITEMS_PER_PAGE
  const convencoesEndIndex = convencoesStartIndex + CONVENCOES_ITEMS_PER_PAGE
  const convencoesPaginados = convencoesOrdenadas.slice(convencoesStartIndex, convencoesEndIndex)

  const toggleConvencoesSort = (key: string) => {
    if (convencoesSortBy === key) {
      setConvencoesSortOrder(convencoesSortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setConvencoesSortBy(key as ConvencaoSortKey)
      setConvencoesSortOrder('asc')
    }
    setConvencoesCurrentPage(1)
  }

  const handleConvencoesPageChange = (page: number) => {
    setConvencoesCurrentPage(page)
  }

  const handleConvencoesPreviousPage = () => {
    if (convencoesCurrentPage > 1) {
      setConvencoesCurrentPage(convencoesCurrentPage - 1)
    }
  }

  const handleConvencoesNextPage = () => {
    if (convencoesCurrentPage < convencoesTotalPages) {
      setConvencoesCurrentPage(convencoesCurrentPage + 1)
    }
  }

  const filtered = filter.trim()
    ? clausulas.filter((item) => {
        const query = filter.trim().toLowerCase()
        return (
          item.nome.toLowerCase().includes(query) ||
          (item.descricao ?? '').toLowerCase().includes(query)
        )
      })
    : clausulas

  const tabClass = (tab: ActiveTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const renderClausulasCctTab = () => (
    <div className="space-y-4">
      {convencoesError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{convencoesError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
            Filtros
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                ID SCGP
              </Text>
              <Input
                type="text"
                placeholder="Digite o ID SCGP..."
                value={filtroIdScgp}
                onChange={(e) => setFiltroIdScgp(e.target.value)}
                disabled={loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                N° MTE
              </Text>
              <Input
                type="text"
                placeholder="Digite o N° MTE..."
                value={filtroNumeroMte}
                onChange={(e) => setFiltroNumeroMte(e.target.value)}
                disabled={loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                CNPJ Entidade Patronal
              </Text>
              <Input
                type="text"
                placeholder="Digite o CNPJ patronal..."
                value={filtroCnpjEntidadePatronal}
                onChange={(e) => setFiltroCnpjEntidadePatronal(e.target.value)}
                disabled={loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                CNPJ Entidade Laboral
              </Text>
              <Input
                type="text"
                placeholder="Digite o CNPJ laboral..."
                value={filtroCnpjEntidadeLaboral}
                onChange={(e) => setFiltroCnpjEntidadeLaboral(e.target.value)}
                disabled={loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Cláusula
              </Text>
              <CheckboxMultiSelect
                options={clausulaFilterOptions}
                selected={filtroClausulaIds}
                onChange={setFiltroClausulaIds}
                placeholder="Todas as cláusulas"
                disabled={loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Status
              </Text>
              <CheckboxMultiSelect
                options={STATUS_FILTER_OPTIONS}
                selected={filtroStatuses}
                onChange={setFiltroStatuses}
                placeholder="Todos os status"
                disabled={loadingConvencoes}
                className="w-full"
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="min-w-0">
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Vigência início
                </Text>
                <div className="flex flex-wrap gap-2 items-end">
                  <Select
                    value={operadorFiltroInicioVigencia}
                    onChange={(e) => setOperadorFiltroInicioVigencia(e.target.value as 'igual' | 'a_partir_de')}
                    disabled={loadingConvencoes}
                    className="w-28 shrink-0"
                  >
                    <option value="igual">Igual</option>
                    <option value="a_partir_de">A partir de</option>
                  </Select>
                  <Select
                    value={tipoFiltroDataInicio}
                    onChange={(e) => {
                      setTipoFiltroDataInicio(e.target.value as 'data' | 'mes' | 'ano')
                      setFiltroDataInicioVigencia('')
                    }}
                    disabled={loadingConvencoes}
                    className="w-24 shrink-0"
                  >
                    <option value="data">Data</option>
                    <option value="mes">Mês</option>
                    <option value="ano">Ano</option>
                  </Select>
                  {tipoFiltroDataInicio === 'data' && (
                    <Input
                      type="date"
                      value={filtroDataInicioVigencia}
                      onChange={(e) => setFiltroDataInicioVigencia(e.target.value)}
                      disabled={loadingConvencoes}
                      className="shrink-0"
                      style={{ width: '180px' }}
                    />
                  )}
                  {tipoFiltroDataInicio === 'mes' && (
                    <Input
                      type="month"
                      value={filtroDataInicioVigencia}
                      onChange={(e) => setFiltroDataInicioVigencia(e.target.value)}
                      disabled={loadingConvencoes}
                      className="shrink-0"
                      style={{ width: '180px' }}
                    />
                  )}
                  {tipoFiltroDataInicio === 'ano' && (
                    <Input
                      type="number"
                      min={1900}
                      max={2100}
                      placeholder="Ano (ex: 2024)"
                      value={filtroDataInicioVigencia || ''}
                      onChange={(e) => setFiltroDataInicioVigencia(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      disabled={loadingConvencoes}
                      className="shrink-0"
                      style={{ width: '100px' }}
                    />
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Vigência fim
                </Text>
                <div className="flex flex-wrap gap-2 items-end">
                  <Select
                    value={operadorFiltroFimVigencia}
                    onChange={(e) => setOperadorFiltroFimVigencia(e.target.value as 'igual' | 'ate')}
                    disabled={loadingConvencoes}
                    className="w-28 shrink-0"
                  >
                    <option value="igual">Igual</option>
                    <option value="ate">Até</option>
                  </Select>
                  <Select
                    value={tipoFiltroDataFim}
                    onChange={(e) => {
                      setTipoFiltroDataFim(e.target.value as 'data' | 'mes' | 'ano')
                      setFiltroDataFimVigencia('')
                    }}
                    disabled={loadingConvencoes}
                    className="w-24 shrink-0"
                  >
                    <option value="data">Data</option>
                    <option value="mes">Mês</option>
                    <option value="ano">Ano</option>
                  </Select>
                  {tipoFiltroDataFim === 'data' && (
                    <Input
                      type="date"
                      value={filtroDataFimVigencia}
                      onChange={(e) => setFiltroDataFimVigencia(e.target.value)}
                      disabled={loadingConvencoes}
                      className="shrink-0"
                      style={{ width: '180px' }}
                    />
                  )}
                  {tipoFiltroDataFim === 'mes' && (
                    <Input
                      type="month"
                      value={filtroDataFimVigencia}
                      onChange={(e) => setFiltroDataFimVigencia(e.target.value)}
                      disabled={loadingConvencoes}
                      className="shrink-0"
                      style={{ width: '180px' }}
                    />
                  )}
                  {tipoFiltroDataFim === 'ano' && (
                    <Input
                      type="number"
                      min={1900}
                      max={2100}
                      placeholder="Ano (ex: 2024)"
                      value={filtroDataFimVigencia || ''}
                      onChange={(e) => setFiltroDataFimVigencia(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      disabled={loadingConvencoes}
                      className="shrink-0"
                      style={{ width: '100px' }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button
              variant="solid"
              size="sm"
              onClick={() => void handleBuscarConvencoes()}
              disabled={loadingConvencoes}
            >
              {loadingConvencoes ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={clearConvencoesFilters}
              disabled={loadingConvencoes || (!hasSearchedConvencoes && !hasConvencoesFilters)}
            >
              Limpar filtros
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Cláusulas das CCTs
            </Heading>
            {hasSearchedConvencoes && (
              <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                {convencoesOrdenadas.length} {convencoesOrdenadas.length === 1 ? 'registro' : 'registros'}
              </span>
            )}
          </div>
        </div>

        {loadingConvencoes ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : !hasSearchedConvencoes ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              Clique em Buscar para carregar os registros.
            </Text>
          </div>
        ) : convencoesOrdenadas.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              Nenhum registro encontrado para os filtros aplicados.
            </Text>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <SortableTableHeader
                    sortKey="id_scgp"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-left w-28"
                  >
                    ID SCGP
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="clausula"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-left"
                  >
                    Cláusula
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="codigo"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-left"
                  >
                    N° MTE
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="status"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-left"
                  >
                    Status
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="num_vidas"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-center"
                  >
                    <span className="leading-tight">
                      Vidas/
                      <br />
                      Subgrupos
                    </span>
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="vigencia_inicio"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-center"
                  >
                    Vigência início
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="vigencia_fim"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-center"
                  >
                    Vigência fim
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Documento
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Download
                  </th>
                  <SortableTableHeader
                    sortKey="clausulas"
                    currentSort={convencoesSortBy}
                    sortOrder={convencoesSortOrder}
                    onSort={toggleConvencoesSort}
                    className="px-4 py-3 text-left"
                  >
                    Cláusulas
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {convencoesPaginados.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {item.cct_buscas?.id_scgp ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.cct_clausulas?.nome || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {item.cct_buscas?.codigo || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {renderBuscaStatus(item.cct_buscas?.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {item.num_vidas != null ? item.num_vidas : '—'} / {item.convenios_qt != null ? item.convenios_qt : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatDateShort(item.cct_buscas?.vigencia_inicio)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatDateShort(item.cct_buscas?.vigencia_fim)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.cct_buscas?.link_documento ? (
                        <a
                          href={item.cct_buscas.link_documento}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:underline text-xs"
                        >
                          Abrir
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.cct_buscas?.link_download ? (
                        <a
                          href={item.cct_buscas.link_download}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:underline text-xs"
                        >
                          Baixar
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatClausulas(item.clausulas)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {convencoesTotalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <Text size="sm">
                    Mostrando {convencoesStartIndex + 1} a {Math.min(convencoesEndIndex, convencoesOrdenadas.length)} de {convencoesOrdenadas.length} registros
                  </Text>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleConvencoesPreviousPage}
                    disabled={convencoesCurrentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Anterior
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, convencoesTotalPages) }, (_, i) => {
                      let pageNumber: number
                      if (convencoesTotalPages <= 5) {
                        pageNumber = i + 1
                      } else if (convencoesCurrentPage <= 3) {
                        pageNumber = i + 1
                      } else if (convencoesCurrentPage >= convencoesTotalPages - 2) {
                        pageNumber = convencoesTotalPages - 4 + i
                      } else {
                        pageNumber = convencoesCurrentPage - 2 + i
                      }

                      return (
                        <button
                          key={pageNumber}
                          type="button"
                          onClick={() => handleConvencoesPageChange(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            convencoesCurrentPage === pageNumber
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      )
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={handleConvencoesNextPage}
                    disabled={convencoesCurrentPage === convencoesTotalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Próximo
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  )

  const renderCadastroTab = () => {
    if (loadingCadastro) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{pageError}</Text>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
                Cláusulas
              </Heading>
              <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                {filtered.length}
                {filter.trim() && filtered.length !== clausulas.length ? ` de ${clausulas.length}` : ''}{' '}
                {filtered.length === 1 ? 'registro' : 'registros'}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtrar por nome ou descrição..."
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-48"
              />
              {canInsert && (
                <Button variant="solid" size="md" onClick={openNew}>
                  <PlusIcon className="h-4 w-4" />
                  Nova Cláusula
                </Button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Text size="sm" color="muted">
                {filter.trim() ? `Nenhum resultado para "${filter}".` : 'Nenhuma cláusula cadastrada.'}
              </Text>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-20">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Descrição
                    </th>
                    {(canUpdate || canDelete) && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24">
                        Ações
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filtered.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {item.id}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {item.nome}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {item.descricao || '—'}
                      </td>
                      {(canUpdate || canDelete) && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {canUpdate && (
                              <button
                                type="button"
                                title="Editar"
                                onClick={() => openEdit(item)}
                                className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                title="Excluir"
                                onClick={() => openDeleteModal(item.id, item.nome)}
                                className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name || 'CCTs • Cláusulas'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
            </div>

            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('clausulas_cct')}
                    className={tabClass('clausulas_cct')}
                  >
                    Cláusulas das CCTs
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('cadastro')}
                    className={tabClass('cadastro')}
                  >
                    Cadastro
                  </button>
                </nav>
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'clausulas_cct' && renderClausulasCctTab()}

              {activeTab === 'cadastro' && renderCadastroTab()}
            </div>
          </div>
        </PermissionGuard>
      </main>

      {formModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {formModal.id ? 'Editar Cláusula' : 'Nova Cláusula'}
              </Text>
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Fechar
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formModal.nome}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev ? { ...prev, nome: e.target.value } : prev
                    )
                  }
                  placeholder="Informe o nome da cláusula..."
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrição
                </label>
                <textarea
                  rows={4}
                  value={formModal.descricao}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev ? { ...prev, descricao: e.target.value } : prev
                    )
                  }
                  placeholder="Descrição opcional..."
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>
            </div>

            {formError && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{formError}</Text>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={handleFormSave} disabled={submittingForm}>
                {submittingForm ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar Exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Você está excluindo: <strong className="text-gray-900 dark:text-white">{deleteModal.label}</strong>
              <br />
              Esta ação não pode ser desfeita.
            </Text>

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="ghost" onClick={closeDeleteModal} disabled={submittingDelete}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={submittingDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CCTClausulas
